/**
 * HMAC signing shared by the API Worker and the job-runner so both sides cannot drift.
 * Uses Web Crypto, available in Workers and Node >= 20.
 */
export const SIGNATURE_HEADER = 'x-detachly-signature';
export const TIMESTAMP_HEADER = 'x-detachly-timestamp';
export const CONTRACTS_VERSION_HEADER = 'x-detachly-contracts';
/** Requests older or newer than this are rejected, which blocks replays. */
export const SIGNATURE_TOLERANCE_SECONDS = 300;
const encoder = new TextEncoder();
async function importKey(secret) {
    return crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
}
function toHex(buffer) {
    return Array.from(new Uint8Array(buffer))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
}
/** Signature payload is `${timestamp}.${rawBody}` — always the raw bytes, never re-serialized JSON. */
export async function signPayload(secret, timestamp, rawBody) {
    const key = await importKey(secret);
    const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(`${timestamp}.${rawBody}`));
    return `v1=${toHex(signature)}`;
}
export function buildSignatureHeaders(signature, timestamp, contractsVersion) {
    return {
        [SIGNATURE_HEADER]: signature,
        [TIMESTAMP_HEADER]: String(timestamp),
        [CONTRACTS_VERSION_HEADER]: contractsVersion,
    };
}
function timingSafeEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let mismatch = 0;
    for (let i = 0; i < a.length; i++) {
        mismatch |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return mismatch === 0;
}
export async function verifySignature(options) {
    const { secret, rawBody } = options;
    const now = options.nowSeconds ?? Math.floor(Date.now() / 1000);
    const timestamp = Number(options.timestamp);
    if (!Number.isFinite(timestamp) || Math.abs(now - timestamp) > SIGNATURE_TOLERANCE_SECONDS) {
        return { ok: false, reason: 'timestamp' };
    }
    if (!options.signature) {
        return { ok: false, reason: 'signature' };
    }
    const expected = await signPayload(secret, timestamp, rawBody);
    return timingSafeEqual(expected, options.signature)
        ? { ok: true }
        : { ok: false, reason: 'signature' };
}
//# sourceMappingURL=signing.js.map